/**
 * Every post: its address, its dates and its share card — and nothing else.
 *
 * Kept apart from the bodies on purpose. `src/routes.ts` reads this file to
 * know which URLs to prerender and list in the sitemap, and `vite.config.ts`
 * reads `routes.ts` in Node at build time: so this module imports nothing at
 * runtime and uses no `@/` alias. The text lives in `posts/`, behind `bodies.ts`.
 *
 * A post is a fixed URL — `/poradnik/<slug>` — because a parameterised route is
 * not prerendered on its own; the slugs below are how the build enumerates them.
 *
 * Newest first. `published` is the day it went up; `updated` moves only when a
 * fact in it is re-checked or changed, not when a comma is.
 */
import type { OgPreview } from "../../lib/og";
import { BLOG_FACTS } from "./facts";
import { SPEAKING_CRITERIA, SPEAKING_MAX } from "../../../shared/structure";
import { CERTYFIKAT_VARIANTS, certyfikatVariant } from "../../../shared/certyfikat";

const story = BLOG_FACTS.stateStory.B1;
const grammar = BLOG_FACTS.stateGrammarB1;
const gaps = grammar.tasks.reduce((a, t) => a + t.items, 0);
const bi = BLOG_FACTS.stateBi.B2;
const stateOral = certyfikatVariant("B1").oralMinutes;
const c1 = CERTYFIKAT_VARIANTS.find((v) => v.id === "certyfikat.c1.adults")!;
const c1Bar = c1.pass.kind === "telc" ? 50 : Math.round(c1.pass.ratio * 100);
const discussion = SPEAKING_CRITERIA.find((c) => c.id === "cz3")!.max;

type Card = { title: string; sub: string; chips: string[] };

export interface PostMeta {
  slug: string;
  published: string;
  updated: string;
  /** The picture on the share card — one of the kinds `scripts/og/render.ts` draws. */
  preview: OgPreview;
  /** The share card's text. Title ≤ 56 characters, as `tests/og.test.ts` holds. */
  card: Record<"pl" | "uk" | "ru" | "en", Card>;
}

export const POSTS: PostMeta[] = [
  {
    slug: "tryb-przypuszczajacy",
    published: "2026-11-03",
    updated: "2026-11-03",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Tryb przypuszczający po polsku", sub: "Gdybym, chciałbym: tabele i ćwiczenia", chips: ["chciał → chciałbym", "gdybym miał"] },
      uk: { title: "Умовний спосіб у польській", sub: "Gdybym, chciałbym: таблиці й вправи", chips: ["chciał → chciałbym", "gdybym miał"] },
      ru: { title: "Сослагательное наклонение в польском", sub: "Gdybym, chciałbym: таблицы и упражнения", chips: ["chciał → chciałbym", "gdybym miał"] },
      en: { title: "The Polish conditional", sub: "Gdybym, chciałbym: tables and exercises", chips: ["chciał → chciałbym", "gdybym miał"] },
    },
  },
  {
    slug: "czas-przeszly",
    published: "2026-11-03",
    updated: "2026-11-03",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Czas przeszły po polsku", sub: "Końcówki, -li czy -ły, formy nieregularne", chips: ["robiłem / robiłam", "mieli, nie miali"] },
      uk: { title: "Минулий час у польській", sub: "Закінчення, -li чи -ły, неправильні форми", chips: ["robiłem / robiłam", "mieli, nie miali"] },
      ru: { title: "Прошедшее время в польском", sub: "Окончания, -li или -ły, неправильные формы", chips: ["robiłem / robiłam", "mieli, nie miali"] },
      en: { title: "The Polish past tense", sub: "Endings, -li or -ły, irregular forms", chips: ["robiłem / robiłam", "mieli, nie miali"] },
    },
  },
  {
    slug: "rozprawka",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Rozprawka po polsku", sub: "Tekst argumentacyjny na B2: teza i wzór", chips: ["Uważam, że…", "Podsumowując…"] },
      uk: { title: "Rozprawka польською", sub: "Аргументативний текст на B2: зразок", chips: ["Uważam, że…", "Podsumowując…"] },
      ru: { title: "Rozprawka на польском", sub: "Аргументативный текст на B2: образец", chips: ["Uważam, że…", "Podsumowując…"] },
      en: { title: "A rozprawka in Polish", sub: "The B2 argumentative text: a model", chips: ["Uważam, że…", "Podsumowując…"] },
    },
  },
  {
    slug: "zawiadomienie",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Zawiadomienie po polsku", sub: "Co, kiedy, gdzie, dlaczego: układ i wzór", chips: ["Uprzejmie informujemy", "Za utrudnienia przepraszamy"] },
      uk: { title: "Zawiadomienie польською", sub: "Що, коли, де, чому: будова і зразок", chips: ["Uprzejmie informujemy", "Za utrudnienia przepraszamy"] },
      ru: { title: "Zawiadomienie на польском", sub: "Что, когда, где, почему: образец", chips: ["Uprzejmie informujemy", "Za utrudnienia przepraszamy"] },
      en: { title: "A notice in Polish", sub: "What, when, where, why: layout and a model", chips: ["Uprzejmie informujemy", "Za utrudnienia przepraszamy"] },
    },
  },
  {
    slug: "opis",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Opis miejsca po polsku", sub: "Szczegóły po kolei i atmosfera: wzór", chips: ["na środku, w głębi", "Pachnie kawą…"] },
      uk: { title: "Опис місця польською", sub: "Деталі по черзі й атмосфера: зразок", chips: ["na środku, w głębi", "Pachnie kawą…"] },
      ru: { title: "Описание места на польском", sub: "Детали по порядку и атмосфера: образец", chips: ["na środku, w głębi", "Pachnie kawą…"] },
      en: { title: "Describing a place in Polish", sub: "Details in order and atmosphere: a model", chips: ["na środku, w głębi", "Pachnie kawą…"] },
    },
  },
  {
    slug: "reklama",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Reklama po polsku", sub: "Krótki tekst na egzamin B2: układ i wzór", chips: ["Masz dość…?", "Zapisz się już dziś!"] },
      uk: { title: "Реклама польською", sub: "Короткий текст для іспиту B2: зразок", chips: ["Masz dość…?", "Zapisz się już dziś!"] },
      ru: { title: "Реклама на польском", sub: "Короткий текст для экзамена B2: образец", chips: ["Masz dość…?", "Zapisz się już dziś!"] },
      en: { title: "An advert in Polish", sub: "A short text for the B2 exam: a model", chips: ["Masz dość…?", "Zapisz się już dziś!"] },
    },
  },
  {
    slug: "list-formalny",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "List formalny po polsku", sub: "E-mail oficjalny na egzamin: układ i wzór", chips: ["Szanowni Państwo,", "Z poważaniem"] },
      uk: { title: "Офіційний лист польською", sub: "Офіційний e-mail на іспит: будова і зразок", chips: ["Szanowni Państwo,", "Z poważaniem"] },
      ru: { title: "Официальное письмо на польском", sub: "Официальный e-mail: структура и образец", chips: ["Szanowni Państwo,", "Z poważaniem"] },
      en: { title: "A formal letter in Polish", sub: "A formal e-mail for the exam: a model", chips: ["Szanowni Państwo,", "Z poważaniem"] },
    },
  },
  {
    slug: "list-prywatny",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "List prywatny po polsku", sub: "E-mail do kolegi na egzamin: układ i wzór", chips: ["Cześć Kasiu!", "Ściskam mocno"] },
      uk: { title: "Приватний лист польською", sub: "E-mail другові на іспит: будова і зразок", chips: ["Cześć Kasiu!", "Ściskam mocno"] },
      ru: { title: "Личное письмо на польском", sub: "E-mail другу на экзамен: структура, образец", chips: ["Cześć Kasiu!", "Ściskam mocno"] },
      en: { title: "A private letter in Polish", sub: "An e-mail to a friend: layout and a model", chips: ["Cześć Kasiu!", "Ściskam mocno"] },
    },
  },
  {
    slug: "recenzja",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Recenzja i opinia po polsku", sub: "Film, książka, miejsce: układ i wzór", chips: ["Polecam każdemu, kto…", "Moja ocena: 8/10"] },
      uk: { title: "Рецензія польською", sub: "Фільм, книжка, місце: будова і зразок", chips: ["Polecam każdemu, kto…", "Moja ocena: 8/10"] },
      ru: { title: "Рецензия на польском", sub: "Фильм, книга, место: структура и образец", chips: ["Polecam każdemu, kto…", "Moja ocena: 8/10"] },
      en: { title: "A review in Polish", sub: "Film, book, place: layout and a model", chips: ["Polecam każdemu, kto…", "Moja ocena: 8/10"] },
    },
  },
  {
    slug: "charakterystyka",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Charakterystyka osoby po polsku", sub: "Cechy z przykładami: układ, zwroty, wzór", chips: ["jest uczynna — bo…", "Bardzo ją cenię za…"] },
      uk: { title: "Характеристика польською", sub: "Риси з прикладами: будова, звороти, зразок", chips: ["jest uczynna — bo…", "Bardzo ją cenię za…"] },
      ru: { title: "Характеристика на польском", sub: "Черты с примерами: структура и образец", chips: ["jest uczynna — bo…", "Bardzo ją cenię za…"] },
      en: { title: "A character sketch in Polish", sub: "Traits with examples: layout and a model", chips: ["jest uczynna — bo…", "Bardzo ją cenię za…"] },
    },
  },
  {
    slug: "esej",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Esej po polsku", sub: "Esej szkolny na egzamin: układ, zwroty, wzór", chips: ["Często słyszy się, że…", "Podsumowując…"] },
      uk: { title: "Есей польською", sub: "Шкільний есей на іспит: будова і зразок", chips: ["Często słyszy się, że…", "Podsumowując…"] },
      ru: { title: "Эссе на польском", sub: "Школьное эссе на экзамен: структура и образец", chips: ["Często słyszy się, że…", "Podsumowując…"] },
      en: { title: "An essay in Polish", sub: "The school essay for the exam: layout, model", chips: ["Często słyszy się, że…", "Podsumowując…"] },
    },
  },
  {
    slug: "sprawozdanie",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Sprawozdanie po polsku", sub: "Relacja z wydarzenia: układ, zwroty, wzór", chips: ["najpierw → następnie", "odbył się…"] },
      uk: { title: "Sprawozdanie польською", sub: "Звіт про подію: будова, звороти, зразок", chips: ["najpierw → następnie", "odbył się…"] },
      ru: { title: "Sprawozdanie на польском", sub: "Отчёт о событии: структура, обороты, образец", chips: ["najpierw → następnie", "odbył się…"] },
      en: { title: "A sprawozdanie in Polish", sub: "Reporting an event: layout, phrases, model", chips: ["najpierw → następnie", "odbył się…"] },
    },
  },
  {
    slug: "zyczenia",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Życzenia po polsku", sub: "Urodziny, imieniny, jubileusz: układ i wzór", chips: ["życzę Ci zdrowia", "Sto lat!"] },
      uk: { title: "Życzenia польською", sub: "Привітання: будова, звороти і зразок", chips: ["życzę Ci zdrowia", "Sto lat!"] },
      ru: { title: "Życzenia на польском", sub: "Поздравление: структура, обороты, образец", chips: ["życzę Ci zdrowia", "Sto lat!"] },
      en: { title: "Życzenia in Polish", sub: "Good wishes: layout, phrases and a model", chips: ["życzę Ci zdrowia", "Sto lat!"] },
    },
  },
  {
    slug: "pozdrowienia",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Pozdrowienia po polsku", sub: "Kartka z wakacji na egzamin: układ i wzór", chips: ["Pozdrowienia z…!", "Ściskam mocno"] },
      uk: { title: "Pozdrowienia польською", sub: "Листівка з відпочинку: будова і зразок", chips: ["Pozdrowienia z…!", "Ściskam mocno"] },
      ru: { title: "Pozdrowienia на польском", sub: "Открытка с отдыха: структура и образец", chips: ["Pozdrowienia z…!", "Ściskam mocno"] },
      en: { title: "Pozdrowienia in Polish", sub: "A holiday postcard for the exam", chips: ["Pozdrowienia z…!", "Ściskam mocno"] },
    },
  },
  {
    slug: "przyimki-miejsca",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Przyimki miejsca po polsku", sub: "W czy na, do czy na: tabela i ćwiczenia", chips: ["w banku / na poczcie", "do sklepu / na dworzec"] },
      uk: { title: "Прийменники місця в польській", sub: "W чи na, do чи na: таблиця і вправи", chips: ["w banku / na poczcie", "do sklepu / na dworzec"] },
      ru: { title: "Предлоги места в польском", sub: "W или na, do или na: таблица и упражнения", chips: ["w banku / na poczcie", "do sklepu / na dworzec"] },
      en: { title: "Polish prepositions of place", sub: "W or na, do or na: a table and exercises", chips: ["w banku / na poczcie", "do sklepu / na dworzec"] },
    },
  },
  {
    slug: "skarga",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Skarga i reklamacja po polsku", sub: "Układ listu, zwroty i wzór z egzaminu B2", chips: ["Szanowni Państwo", "Z poważaniem"] },
      uk: { title: "Скарга і рекламація польською", sub: "Будова листа, звороти і зразок з іспиту B2", chips: ["Szanowni Państwo", "Z poważaniem"] },
      ru: { title: "Жалоба и рекламация на польском", sub: "Структура письма, обороты и образец B2", chips: ["Szanowni Państwo", "Z poważaniem"] },
      en: { title: "A complaint in Polish", sub: "Layout, phrases and a B2 model", chips: ["Szanowni Państwo", "Z poważaniem"] },
    },
  },
  {
    slug: "alfabet",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "words" },
    card: {
      pl: { title: "Alfabet polski i wymowa", sub: "32 litery, ą, ę, sz, cz, rz — tabela", chips: ["ó = u", "rz = ż"] },
      uk: { title: "Польський алфавіт і вимова", sub: "32 літери, ą, ę, sz, cz, rz — таблиця", chips: ["ó = u", "rz = ż"] },
      ru: { title: "Польский алфавит и произношение", sub: "32 буквы, ą, ę, sz, cz, rz — таблица", chips: ["ó = u", "rz = ż"] },
      en: { title: "The Polish alphabet", sub: "32 letters, ą, ę, sz, cz, rz — a table", chips: ["ó = u", "rz = ż"] },
    },
  },
  {
    slug: "rezydent-ue",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "badge", level: "B1", accepts: "both" },
    card: {
      pl: { title: "Karta rezydenta długoterminowego UE", sub: "Wymagania, polski B1, opłata i terminy", chips: ["5 lat pobytu", "640 zł"] },
      uk: { title: "Карта довгострокового резидента ЄС", sub: "Вимоги, польська B1, збір і строки", chips: ["5 років перебування", "640 zł"] },
      ru: { title: "Карта долгосрочного резидента ЕС", sub: "Требования, польский B1, сбор и сроки", chips: ["5 лет пребывания", "640 zł"] },
      en: { title: "The EU long-term resident card", sub: "Requirements, Polish B1, fee and timing", chips: ["5 years' stay", "640 zł"] },
    },
  },
  {
    slug: "bez-egzaminu",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "badge", level: "B1", accepts: "state" },
    card: {
      pl: { title: "Certyfikat B1 z polskiego bez egzaminu", sub: "Kiedy wystarczy świadectwo polskiej szkoły", chips: ["obywatelstwo: świadectwo", "rezydent UE: też dyplom"] },
      uk: { title: "Сертифікат B1 з польської без іспиту", sub: "Коли досить свідоцтва польської школи", chips: ["громадянство: свідоцтво", "резидент ЄС: і диплом"] },
      ru: { title: "Сертификат B1 по польскому без экзамена", sub: "Когда хватит аттестата польской школы", chips: ["гражданство: аттестат", "резидент ЕС: и диплом"] },
      en: { title: "Polish B1 certificate without the exam", sub: "When a Polish school certificate will do", chips: ["citizenship: school", "EU resident: a degree too"] },
    },
  },
  {
    slug: "zaimki",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Zaimki osobowe po polsku", sub: "Mi czy mnie, go czy jego, niego: tabela i ćwiczenia", chips: ["daj mi / mnie daj", "do niego / do jego domu"] },
      uk: { title: "Особові займенники в польській", sub: "Mi чи mnie, go чи jego, niego: таблиця і вправи", chips: ["daj mi / mnie daj", "do niego / do jego domu"] },
      ru: { title: "Личные местоимения в польском", sub: "Mi или mnie, go или jego, niego: таблица", chips: ["daj mi / mnie daj", "do niego / do jego domu"] },
      en: { title: "Polish personal pronouns", sub: "Mi or mnie, go or jego, niego: table, exercises", chips: ["daj mi / mnie daj", "do niego / do jego domu"] },
    },
  },
  {
    slug: "przymiotniki",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Przymiotniki po polsku", sub: "Stopniowanie i odmiana: tabele i ćwiczenia", chips: ["ładny → ładniejszy", "dobry → lepszy"] },
      uk: { title: "Прикметники в польській мові", sub: "Ступені порівняння і відмінювання", chips: ["ładny → ładniejszy", "dobry → lepszy"] },
      ru: { title: "Прилагательные в польском", sub: "Степени сравнения и склонение", chips: ["ładny → ładniejszy", "dobry → lepszy"] },
      en: { title: "Polish adjectives", sub: "Comparison and declension: tables, exercises", chips: ["ładny → ładniejszy", "dobry → lepszy"] },
    },
  },
  {
    slug: "liczba-mnoga",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Liczba mnoga po polsku", sub: "Studenci czy studenty: reguły, tabele, ćwiczenia", chips: ["student → studenci", "byli / były"] },
      uk: { title: "Множина в польській мові", sub: "Studenci чи studenty: правила, таблиці, вправи", chips: ["student → studenci", "byli / były"] },
      ru: { title: "Множественное число в польском", sub: "Studenci или studenty: правила, таблицы, упражнения", chips: ["student → studenci", "byli / były"] },
      en: { title: "Polish plural nouns", sub: "Studenci or studenty: rules, tables, exercises", chips: ["student → studenci", "byli / były"] },
    },
  },
  {
    slug: "tryb-rozkazujacy",
    published: "2026-10-29",
    updated: "2026-10-29",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Tryb rozkazujący po polsku", sub: "Jak go tworzyć: tabele, wyjątki, ćwiczenia", chips: ["czyta → czytaj", "nie otwieraj / otwórz"] },
      uk: { title: "Наказовий спосіб у польській", sub: "Як утворювати: таблиці, винятки, вправи", chips: ["czyta → czytaj", "nie otwieraj / otwórz"] },
      ru: { title: "Повелительное наклонение в польском", sub: "Как образовать: таблицы, исключения, упражнения", chips: ["czyta → czytaj", "nie otwieraj / otwórz"] },
      en: { title: "The Polish imperative", sub: "How to form it: tables, exceptions, exercises", chips: ["czyta → czytaj", "nie otwieraj / otwórz"] },
    },
  },
  {
    slug: "pan-pani",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "writing" },
    card: {
      pl: { title: "Pan, Pani, Państwo po polsku", sub: "Jak zwracać się grzecznie: odmiana i listy", chips: ["czasownik w 3. osobie", "wielka litera w listach"] },
      uk: { title: "Pan, Pani, Państwo польською", sub: "Як ввічливо звертатися: відмінювання і листи", chips: ["дієслово в 3-й особі", "велика літера в листах"] },
      ru: { title: "Pan, Pani, Państwo по-польски", sub: "Как вежливо обращаться: склонение и письма", chips: ["глагол в 3-м лице", "большая буква в письмах"] },
      en: { title: "Pan, Pani, Państwo in Polish", sub: "Polite address: declension and letters", chips: ["a third-person verb", "capitals in letters"] },
    },
  },
  {
    slug: "egzamin-c1",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "sittings" },
    card: {
      pl: { title: "Egzamin C1 z polskiego: jak wygląda", sub: "Egzamin państwowy C1 a B2: czas, próg, terminy", chips: [`${c1Bar}% z każdej części`, `ustna do ${c1.oralMinutes} minut`] },
      uk: { title: "Іспит C1 з польської: як виглядає", sub: "Державний іспит C1 і B2: час, поріг, терміни", chips: [`${c1Bar}% з кожної частини`, `усна до ${c1.oralMinutes} хвилин`] },
      ru: { title: "Экзамен C1 по польскому: как выглядит", sub: "Государственный экзамен C1 и B2: время, порог, сроки", chips: [`${c1Bar}% из каждой части`, `устная до ${c1.oralMinutes} минут`] },
      en: { title: "The Polish C1 exam: what it looks like", sub: "The state C1 exam against B2: time, bar, dates", chips: [`${c1Bar}% in every part`, `oral up to ${c1.oralMinutes} minutes`] },
    },
  },
  {
    slug: "zaproszenie",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Jak napisać zaproszenie po polsku", sub: "Wzór i przykłady z egzaminu państwowego B1", chips: [`w ${BLOG_FACTS.stateShortForms.B1.invitation.papers} z ${BLOG_FACTS.stateShortForms.B1.papers} arkuszy B1`, "do znajomych i od instytucji"] },
      uk: { title: "Як написати zaproszenie польською", sub: "Шаблон і приклади з державного іспиту B1", chips: [`в ${BLOG_FACTS.stateShortForms.B1.invitation.papers} з ${BLOG_FACTS.stateShortForms.B1.papers} наборів B1`, "знайомим і від установи"] },
      ru: { title: "Как написать zaproszenie по-польски", sub: "Шаблон и примеры с государственного экзамена B1", chips: [`в ${BLOG_FACTS.stateShortForms.B1.invitation.papers} из ${BLOG_FACTS.stateShortForms.B1.papers} наборов B1`, "знакомым и от учреждения"] },
      en: { title: "How to write a zaproszenie in Polish", sub: "A template and examples from the state B1 exam", chips: [`on ${BLOG_FACTS.stateShortForms.B1.invitation.papers} of ${BLOG_FACTS.stateShortForms.B1.papers} B1 papers`, "to friends and from an institution"] },
    },
  },
  {
    slug: "ogloszenie",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Jak napisać ogłoszenie po polsku", sub: "Wzór i przykłady z egzaminu państwowego B1", chips: [`w ${BLOG_FACTS.stateShortForms.B1.announcement.papers} z ${BLOG_FACTS.stateShortForms.B1.papers} arkuszy B1`, "Sprzedam · Zaginął · Szukam"] },
      uk: { title: "Як написати ogłoszenie польською", sub: "Шаблон і приклади з державного іспиту B1", chips: [`у ${BLOG_FACTS.stateShortForms.B1.announcement.papers} з ${BLOG_FACTS.stateShortForms.B1.papers} наборів B1`, "Sprzedam · Zaginął · Szukam"] },
      ru: { title: "Как написать ogłoszenie по-польски", sub: "Шаблон и примеры с государственного экзамена B1", chips: [`в ${BLOG_FACTS.stateShortForms.B1.announcement.papers} из ${BLOG_FACTS.stateShortForms.B1.papers} наборов B1`, "Sprzedam · Zaginął · Szukam"] },
      en: { title: "How to write an ogłoszenie in Polish", sub: "A template and examples from the state B1 exam", chips: [`on ${BLOG_FACTS.stateShortForms.B1.announcement.papers} of ${BLOG_FACTS.stateShortForms.B1.papers} B1 papers`, "Sprzedam · Zaginął · Szukam"] },
    },
  },
  {
    slug: "test-poziomu",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "compare" },
    card: {
      pl: { title: "Test poziomu polskiego: A2, B1, B2", sub: "18 zdań z odpowiedziami, po 6 na poziom", chips: ["gramatyka z przepisów egzaminu", "odpowiedzi pod każdym zdaniem"] },
      uk: { title: "Тест на рівень польської: A2, B1, B2", sub: "18 речень із відповідями, по 6 на рівень", chips: ["граматика з правил іспиту", "відповідь під кожним реченням"] },
      ru: { title: "Тест на уровень польского: A2, B1, B2", sub: "18 предложений с ответами, по 6 на уровень", chips: ["грамматика из правил экзамена", "ответ под каждым предложением"] },
      en: { title: "Polish level test: A2, B1, B2", sub: "18 sentences with answers, 6 per level", chips: ["grammar from the exam's rules", "an answer under every sentence"] },
    },
  },
  {
    slug: "obywatelstwo-wymagania",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "badge", level: "B1", accepts: "state" },
    card: {
      pl: { title: "Obywatelstwo polskie: wymagania", sub: "Wojewoda czy prezydent, lata i certyfikat B1", chips: ["3 lata pobytu stałego", "certyfikat państwowy B1"] },
      uk: { title: "Польське громадянство: вимоги", sub: "Воєвода чи президент, роки і сертифікат B1", chips: ["3 роки постійного проживання", "державний сертифікат B1"] },
      ru: { title: "Польское гражданство: требования", sub: "Воевода или президент, годы и сертификат B1", chips: ["3 года постоянного проживания", "государственный сертификат B1"] },
      en: { title: "Polish citizenship: the requirements", sub: "Voivode or President, the years and a B1 certificate", chips: ["3 years' permanent residence", "a state B1 certificate"] },
    },
  },
  {
    slug: "karta-polaka",
    published: "2026-10-28",
    updated: "2026-10-28",
    preview: { kind: "badge", level: "B1", accepts: "state" },
    card: {
      pl: { title: "Karta Polaka: jaki polski trzeba znać", sub: "Rozmowa z konsulem, a potem obywatelstwo", chips: ["podstawowy: rozmowa", "obywatelstwo: certyfikat B1"] },
      uk: { title: "Karta Polaka: яка польська потрібна", sub: "Розмова з консулом, а потім громадянство", chips: ["базовий: розмова", "громадянство: сертифікат B1"] },
      ru: { title: "Karta Polaka: какой польский нужен", sub: "Беседа с консулом, а потом гражданство", chips: ["базовый: беседа", "гражданство: сертификат B1"] },
      en: { title: "Karta Polaka: how much Polish you need", sub: "The consul's interview, then citizenship", chips: ["basic: a conversation", "citizenship: a B1 certificate"] },
    },
  },
  {
    slug: "poziomy-a2-b1-b2",
    published: "2026-10-17",
    updated: "2026-10-17",
    preview: { kind: "compare" },
    card: {
      pl: { title: "Poziomy A2, B1 i B2 z polskiego: co trzeba umieć", sub: "Według standardów egzaminu państwowego", chips: ["tematy i rozmowa", "formy pisemne", "gramatyka"] },
      uk: { title: "Рівні A2, B1 і B2 з польської: що треба вміти", sub: "За стандартами державного іспиту", chips: ["теми й розмова", "письмові форми", "граматика"] },
      ru: { title: "Уровни A2, B1 и B2 по польскому: что нужно уметь", sub: "По стандартам государственного экзамена", chips: ["темы и разговор", "письменные формы", "грамматика"] },
      en: { title: "Polish A2, B1 and B2: what each level expects", sub: "From the state exam's standards", chips: ["topics and conversation", "written forms", "grammar"] },
    },
  },
  {
    slug: "liczebniki",
    published: "2026-10-17",
    updated: "2026-10-17",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Polskie liczebniki: dwa, dwie, dwaj, daty, godziny", sub: "Tabele i ćwiczenia przed egzaminem B1", chips: ["2 lata, 5 lat", "dwudziestego drugiego czerwca", "o siódmej"] },
      uk: { title: "Польські числівники: dwa, dwie, dwaj, дати, години", sub: "Таблиці та вправи перед іспитом B1", chips: ["2 lata, 5 lat", "dwudziestego drugiego czerwca", "o siódmej"] },
      ru: { title: "Польские числительные: dwa, dwie, dwaj, даты, время", sub: "Таблицы и упражнения перед экзаменом B1", chips: ["2 lata, 5 lat", "dwudziestego drugiego czerwca", "o siódmej"] },
      en: { title: "Polish numerals: dwa, dwie, dwaj, dates, times", sub: "Tables and exercises before the B1 exam", chips: ["2 lata, 5 lat", "dwudziestego drugiego czerwca", "o siódmej"] },
    },
  },
  {
    slug: "czasowniki",
    published: "2026-10-17",
    updated: "2026-10-17",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Polskie czasowniki: odmiana, czasy i aspekt", sub: "Tabele i ćwiczenia przed egzaminem B1", chips: ["czas przeszły i przyszły", "pary aspektowe", "ćwiczenia z odpowiedziami"] },
      uk: { title: "Польські дієслова: відмінювання, часи й вид", sub: "Таблиці та вправи перед іспитом B1", chips: ["минулий і майбутній час", "видові пари", "вправи з відповідями"] },
      ru: { title: "Польские глаголы: спряжение, времена и вид", sub: "Таблицы и упражнения перед экзаменом B1", chips: ["прошедшее и будущее время", "видовые пары", "упражнения с ответами"] },
      en: { title: "Polish verbs: conjugation, tenses and aspect", sub: "Tables and exercises before the B1 exam", chips: ["past and future", "aspect pairs", "exercises with answers"] },
    },
  },
  {
    slug: "przypadki",
    published: "2026-10-17",
    updated: "2026-10-17",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Polskie przypadki: tabela końcówek i ćwiczenia", sub: "Siedem przypadków przed egzaminem B1", chips: ["liczba pojedyncza i mnoga", "przyimki", "ćwiczenia z odpowiedziami"] },
      uk: { title: "Польські відмінки: таблиця закінчень і вправи", sub: "Сім відмінків перед іспитом B1", chips: ["однина і множина", "прийменники", "вправи з відповідями"] },
      ru: { title: "Польские падежи: таблица окончаний и упражнения", sub: "Семь падежей перед экзаменом B1", chips: ["единственное и множественное", "предлоги", "упражнения с ответами"] },
      en: { title: "Polish cases: endings tables and exercises", sub: "The seven cases before the B1 exam", chips: ["singular and plural", "prepositions", "exercises with answers"] },
    },
  },
  {
    slug: "dzien-egzaminu",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "sittings" },
    card: {
      pl: { title: "Dzień egzaminu z polskiego: co zabrać", sub: "telc i egzamin państwowy — z przepisów", chips: ["dokument ze zdjęciem", "na państwowym bez ołówka"] },
      uk: { title: "День іспиту з польської: що взяти", sub: "telc і державний іспит — за правилами", chips: ["документ із фото", "на державному без олівця"] },
      ru: { title: "День экзамена по польскому: что взять", sub: "telc и государственный экзамен — по правилам", chips: ["документ с фото", "на государственном без карандаша"] },
      en: { title: "Polish exam day: what to bring", sub: "telc and the state exam — from the rules", chips: ["photo ID", "no pencil at the state exam"] },
    },
  },
  {
    slug: "egzamin-online",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "compare" },
    card: {
      pl: { title: "Egzamin z polskiego online: czy jest uznawany?", sub: "Trzy drogi do certyfikatu i jak sprawdzić ośrodek", chips: ["państwowy: tylko w ośrodku", "telc: papier albo hybryda"] },
      uk: { title: "Іспит з польської онлайн: чи його визнають?", sub: "Три шляхи до сертифіката і як перевірити центр", chips: ["державний: лише в осередку", "telc: папір або гібрид"] },
      ru: { title: "Экзамен по польскому онлайн: признают ли его?", sub: "Три пути к сертификату и как проверить центр", chips: ["государственный: только в центре", "telc: бумага или гибрид"] },
      en: { title: "The Polish exam online: is it recognised?", sub: "Three routes to a certificate, and how to check a centre", chips: ["state exam: at a centre only", "telc: paper or hybrid"] },
    },
  },
  {
    slug: "waznosc-certyfikatu",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "compare" },
    card: {
      pl: { title: "Czy certyfikat z polskiego traci ważność?", sub: "Państwowy B1 i telc — co piszą Komisja i telc", chips: ["państwowy: bezterminowo", "telc: bez terminu ważności"] },
      uk: { title: "Чи втрачає силу сертифікат з польської?", sub: "Державний B1 і telc — що пишуть Комісія і telc", chips: ["державний: безстроково", "telc: без строку дії"] },
      ru: { title: "Теряет ли силу сертификат по польскому?", sub: "Государственный B1 и telc — что пишут Комиссия и telc", chips: ["государственный: бессрочно", "telc: без срока действия"] },
      en: { title: "Does a Polish certificate expire?", sub: "State B1 and telc — what the Commission and telc say", chips: ["state: indefinitely", "telc: no validity period"] },
    },
  },
  {
    slug: "ustny-panstwowy",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "state-oral", level: "B1" },
    card: {
      pl: { title: "Ustny na państwowym B1 i B2: jak ćwiczyć samemu", sub: "Co mówią przepisy i metoda krok po kroku", chips: [`do ${stateOral} minut`, "kryteria nieopublikowane"] },
      uk: { title: "Усний на державному B1 і B2: тренуйся сам", sub: "Що кажуть правила і метод крок за кроком", chips: [`до ${stateOral} хвилин`, "критерії не опубліковані"] },
      ru: { title: "Устный на государственном B1 и B2: тренируйся сам", sub: "Что говорят правила и метод шаг за шагом", chips: [`до ${stateOral} минут`, "критерии не опубликованы"] },
      en: { title: "The state B1 and B2 oral: practising alone", sub: "What the rules say, and a step-by-step method", chips: [`up to ${stateOral} minutes`, "criteria unpublished"] },
    },
  },
  {
    slug: "ustny-telc-ocena",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "telc-oral", part: 2 },
    card: {
      pl: { title: "Jak oceniany jest ustny telc z polskiego", sub: `Pięć kryteriów, dwóch egzaminatorów, ${SPEAKING_MAX} punktów`, chips: ["B1 a B2 w każdej części", `dyskusja: ${discussion} punktów`] },
      uk: { title: "Як оцінюють усний telc з польської", sub: `П’ять критеріїв, двоє екзаменаторів, ${SPEAKING_MAX} балів`, chips: ["B1 і B2 у кожній частині", `дискусія: ${discussion} балів`] },
      ru: { title: "Как оценивают устный telc по польскому", sub: `Пять критериев, два экзаменатора, ${SPEAKING_MAX} баллов`, chips: ["B1 и B2 в каждой части", `дискуссия: ${discussion} баллов`] },
      en: { title: "How the telc Polish oral is marked", sub: `Five criteria, two examiners, ${SPEAKING_MAX} points`, chips: ["B1 against B2 in each part", `discussion: ${discussion} points`] },
    },
  },
  {
    slug: "brak-informacji-b2",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "state-result", bar: 60 },
    card: {
      pl: { title: "TAK, NIE czy BI? Czytanie na państwowym B2", sub: "„Brak informacji” na egzaminie państwowym z polskiego", chips: [`w ${bi.withBi} z ${bi.papers} arkuszy B2`, `${bi.statementsPerTask * bi.pointsPerStatement} punktów`] },
      uk: { title: "TAK, NIE чи BI? Читання на державному B2", sub: "«Brak informacji» на державному іспиті з польської", chips: [`у ${bi.withBi} з ${bi.papers} наборів B2`, `${bi.statementsPerTask * bi.pointsPerStatement} балів`] },
      ru: { title: "TAK, NIE или BI? Чтение на государственном B2", sub: "«Brak informacji» на государственном экзамене по польскому", chips: [`в ${bi.withBi} из ${bi.papers} наборов B2`, `${bi.statementsPerTask * bi.pointsPerStatement} баллов`] },
      en: { title: "TAK, NIE or BI? Reading in the state B2 exam", sub: "\"Brak informacji\" in the Polish state exam", chips: [`on ${bi.withBi} of ${bi.papers} B2 papers`, `${bi.statementsPerTask * bi.pointsPerStatement} points`] },
    },
  },
  {
    slug: "gramatyka-b1",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Gramatyka na państwowym B1: osiem stałych zadań", sub: "Egzamin państwowy z polskiego, poprawność gramatyczna", chips: [`${grammar.tasks.length} zadań`, `${gaps} luk`, `w ${grammar.papers} arkuszach to samo`] },
      uk: { title: "Граматика на державному B1: вісім сталих завдань", sub: "Державний іспит з польської, граматика", chips: [`${grammar.tasks.length} завдань`, `${gaps} пропусків`, `однаково в ${grammar.papers} наборах`] },
      ru: { title: "Грамматика на государственном B1: восемь заданий", sub: "Государственный экзамен по польскому, грамматика", chips: [`${grammar.tasks.length} заданий`, `${gaps} пропусков`, `одинаково в ${grammar.papers} наборах`] },
      en: { title: "State exam B1 grammar: the same eight tasks", sub: "The Polish state exam, grammar block", chips: [`${grammar.tasks.length} tasks`, `${gaps} gaps`, `the same on ${grammar.papers} papers`] },
    },
  },
  {
    slug: "opowiadanie-b1",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "state-writing" },
    card: {
      pl: { title: "Opowiadanie na państwowym B1: jak je napisać", sub: "Egzamin państwowy z polskiego, część pisemna", chips: [`${story.papersWithStory} z ${story.papers} arkuszy B1`, `${story.words.min} słów`, "pełny przykład"] },
      uk: { title: "Оповідання на державному B1: як написати", sub: "Державний іспит з польської, письмова частина", chips: [`${story.papersWithStory} з ${story.papers} наборів B1`, `${story.words.min} слів`, "повний приклад"] },
      ru: { title: "Рассказ на государственном B1: как написать", sub: "Государственный экзамен по польскому, письменная часть", chips: [`${story.papersWithStory} из ${story.papers} наборов B1`, `${story.words.min} слов`, "полный пример"] },
      en: { title: "The story in the state B1 exam: how to write it", sub: "The Polish state exam, written part", chips: [`${story.papersWithStory} of ${story.papers} B1 papers`, `${story.words.min} words`, "a full example"] },
    },
  },
  {
    slug: "jedna-czesc-ponizej-progu",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "state-result", bar: 50 },
    card: {
      pl: { title: "Jedna część poniżej progu: co dalej?", sub: "Egzamin państwowy z polskiego B1 i B2", chips: ["cały egzamin od nowa", "ponowne sprawdzenie: 21 dni"] },
      uk: { title: "Одна частина нижче порога: що далі?", sub: "Державний іспит з польської B1 і B2", chips: ["увесь іспит заново", "повторна перевірка: 21 день"] },
      ru: { title: "Одна часть ниже порога: что дальше?", sub: "Государственный экзамен по польскому B1 и B2", chips: ["весь экзамен заново", "повторная проверка: 21 день"] },
      en: { title: "One part below the bar: what next?", sub: "The Polish state exam, B1 and B2", chips: ["the whole exam again", "re-marking: 21 days"] },
    },
  },
  {
    slug: "ile-razy-nagranie",
    published: "2026-10-03",
    updated: "2026-10-03",
    preview: { kind: "papers" },
    card: {
      pl: { title: "Ile razy usłyszysz nagranie na egzaminie?", sub: "Słuchanie na telc i na egzaminie państwowym", chips: ["telc: raz", "państwowy: zwykle dwa razy"] },
      uk: { title: "Скільки разів звучить запис на іспиті?", sub: "Слухання на telc і на державному іспиті", chips: ["telc: раз", "державний: зазвичай двічі"] },
      ru: { title: "Сколько раз звучит запись на экзамене?", sub: "Аудирование на telc и на государственном экзамене", chips: ["telc: раз", "государственный: обычно дважды"] },
      en: { title: "How many times is the recording played?", sub: "Listening in telc and in the Polish state exam", chips: ["telc: once", "state exam: usually twice"] },
    },
  },
];

export const BLOG_PATH = "/poradnik";
export const postPath = (slug: string) => `${BLOG_PATH}/${slug}`;
export const POST_PATHS: string[] = POSTS.map((p) => postPath(p.slug));
